import { DatePicker, Pagination, Table, Tag } from "rsuite";
import { useEffect, useMemo, useRef, useState } from "react";
import { Card } from "./card.component";
import { TextCell } from "./tableItem.component";
import { useAppDispatch, useAppSelector } from "../core/redux/ReduxStore";
import upSolidIcon from "../assets/img/header-chevron-up-solid.svg";
import downSolidIcon from "../assets/img/header-chevron-up-solid.svg";
import {
  ApiSchemaCaregiversReport,
  ApiSchemaPaginatedCaregiverReport,
} from "../models/api/apiSuperAdminDashboard";
import { ActionsCaregiversReport } from "../core/redux/slices/sliceCaregiversReport";
import { UtilityDate } from "../utils/dateUtils";
import {
  useTablePaginationSearchFilter,
  IPaginationSearchFilter,
} from "../hooks";
import { AppRouteApi } from "../config/appRoute";

const { Column, HeaderCell, Cell } = Table;

type TableData = ApiSchemaCaregiversReport & {
  sl?: number;
};

export const CaregiversReport = () => {
  const triggerRef = useRef<any>();
  let [tableData, setTableData] = useState<TableData[]>([]);
  const [startDate, setStartDate] = useState<Date>(
    UtilityDate.getStartOfPreviousMonth()
  );
  const [endDate, setEndDate] = useState<Date>(
    UtilityDate.getEndOfPreviousMonth()
  );
  const dispatch = useAppDispatch();

  const { pageNumber, pageSize, nextPageToken, isLoading } = useAppSelector(
    (state) => state.caregiversReportTable
  );
  const apiParams = useMemo<IPaginationSearchFilter["customParams"]>(() => {
    return {
      StartDate: startDate
        ? UtilityDate.getMinimumPossibleHour(startDate).toISOString()
        : "",
      EndDate: endDate
        ? UtilityDate.getMaxPossibleHour(endDate).toISOString()
        : "",
      PageNumber: pageNumber,
      NextPageToken: nextPageToken,
    };
  }, [startDate, endDate, pageNumber, nextPageToken]);

  const { apiData } = useTablePaginationSearchFilter<
    ApiSchemaPaginatedCaregiverReport,
    typeof ActionsCaregiversReport
  >(ActionsCaregiversReport, {
    apiRoute: AppRouteApi.Reporting.CaregiversReport(),
    customParams: apiParams,
  });

  const isValidStartDate = (date?: Date) => {
    if (!date) return false;
    if (!endDate) return true;
    if (date <= endDate) return true;
    return false;
  };

  const isValidEndDate = (date?: Date) => {
    if (!date) return false;
    if (!startDate) return true;
    if (date >= startDate) return true;
    return false;
  };

  useEffect(() => {
    setTableData(
      apiData?.list?.map((Task, index) => ({
        sl: (pageNumber - 1) * pageSize + index + 1,
        ...Task,
      })) ?? []
    );
  }, [apiData, pageNumber, pageSize]);

  useEffect(() => {
    return () => {
      dispatch(ActionsCaregiversReport.resetData());
    };
  }, [dispatch]);

  return (
    <Card
      header={
        <div className="row align-items-center  justify-content-between">
          <div className="col-md-8 col-12 content-box-title">
            <div>
              <p className="text-capitalize my-1 text-green-200">
                Caregivers Report
              </p>
            </div>
            <div className="collapse-icon"></div>
          </div>
          <a
            className="btn  box-collapse-btn me-1"
            data-bs-toggle="collapse"
            href="#caregiver-report-table"
            role="button"
            aria-expanded="false"
            aria-controls="caregiver-report-table"
          >
            <img
              src={upSolidIcon}
              alt="up"
              className="img-fluid svg-btn collapse-close"
            />
            <img
              src={downSolidIcon}
              alt="down"
              className="img-fluid svg-btn collapse-open"
            />
          </a>
        </div>
      }
      className="h-100"
    >
      <div
        className="collapse show clearfix position-relative"
        id="caregiver-report-table"
      >
        <div className="row g-0 align-items-center pb-3">
          <div className="col-auto">
            <span className="pe-1">Start Date</span>
          </div>
          <div className="col-4">
            <DatePicker
              disabledDate={(date?: Date) => !isValidStartDate(date)}
              onChange={setStartDate}
              value={startDate}
              format="MM-dd-yyyy"
            />
          </div>

          <div className="col-auto ps-2">
            <span className="pe-1">End Date</span>
          </div>

          <div className="col-4">
            <DatePicker
              disabledDate={(date?: Date) => !isValidEndDate(date)}
              onChange={setEndDate}
              value={endDate}
              format="MM-dd-yyyy"
            />
          </div>
        </div>
        <Table
          autoHeight
          data={tableData}
          loading={isLoading}
          onScroll={() => triggerRef.current?.close()}
          renderEmpty={() => (
            <div className="d-flex justify-content-center text-muted align-items-center h-100 text-center p-5">
              <h3 className="fw-light">No Data Available</h3>
            </div>
          )}
        >
          <Column align="center" width={60}>
            <HeaderCell className="fw-bold text-black">
              <span title="S/L">S/L</span>
            </HeaderCell>
            <TextCell component="Report" dataKey="sl" />
          </Column>

          <Column flexGrow={1}>
            <HeaderCell className="fw-bold text-black">
              <span title="Name">Name</span>
            </HeaderCell>
            <TextCell component="Report" dataKey="careGiverName" />
          </Column>

          <Column align="center" flexGrow={1}>
            <HeaderCell className="fw-bold text-black">
              <span title="Code">Missed Check In Count</span>
            </HeaderCell>
            <Cell>
              {(rowData: TableData) => (
                <Tag color="red">{rowData.missedCheckInCount}</Tag>
              )}
            </Cell>
          </Column>
          <Column align="center" flexGrow={1}>
            <HeaderCell className="fw-bold text-black">
              <span title="Code">Missed Check Out Count</span>
            </HeaderCell>
            <Cell>
              {(rowData: TableData) => (
                <Tag color="orange">{rowData.missedCheckOutCount}</Tag>
              )}
            </Cell>
          </Column>
          <Column align="center" flexGrow={1}>
            <HeaderCell className="fw-bold text-black">
              <span title="Code">Ongoing Shift</span>
            </HeaderCell>
            <Cell>
              {(rowData: TableData) => (
                <Tag color="green">{rowData.ongoingShift}</Tag>
              )}
            </Cell>
          </Column>
        </Table>
        {apiData?.list && (
          <div className="d-flex justify-content-end mt-3">
            <Pagination
              total={apiData?.list?.length}
              pages={Math.ceil(apiData?.totalCount / pageSize)}
              maxButtons={6}
              next={true}
              prev={true}
              activePage={pageNumber}
              onSelect={(page) =>
                dispatch(ActionsCaregiversReport.goToPage(page))
              }
            />
          </div>
        )}
      </div>
    </Card>
  );
};
